/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * FormInput Component - Enhanced input field with validation
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import PropTypes from 'prop-types'
import { AlertCircle, CheckCircle2 } from 'lucide-react'

/**
 * FormInput Component
 *
 * Enhanced input field with:
 * - Floating label design
 * - Inline validation with smooth error messages
 * - Success state indicators
 * - Icon support
 *
 * @param {Object} props
 * @param {string} props.id - Input ID
 * @param {string} props.name - Input name
 * @param {string} props.type - Input type (text, email, etc.)
 * @param {string} props.label - Input label
 * @param {string} props.value - Input value
 * @param {Function} props.onChange - Change handler
 * @param {Function} props.onBlur - Blur handler
 * @param {string} props.error - Error message
 * @param {boolean} props.success - Success state
 * @param {boolean} props.required - Required field
 * @param {boolean} props.disabled - Disabled state
 * @param {boolean} props.readOnly - Read-only state
 * @param {string} props.placeholder - Placeholder text
 * @param {React.ReactNode} props.icon - Leading icon
 * @param {string} props.autoComplete - Autocomplete attribute
 */
const FormInput = ({
  id,
  name,
  type = 'text',
  label,
  value,
  onChange,
  onBlur,
  error,
  success,
  required,
  disabled,
  readOnly,
  placeholder,
  icon,
  autoComplete,
  ...rest
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const hasValue = Boolean(value && value.length > 0)
  const isFloating = isFocused || hasValue

  return (
    <div className="relative">
      {/* Input Container */}
      <div className="relative">
        {/* Leading Icon */}
        {icon && (
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
            {icon}
          </div>
        )}

        {/* Input Field */}
        <input
          id={id}
          name={name}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={(e) => {
            setIsFocused(false)
            onBlur && onBlur(e)
          }}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          placeholder=""
          autoComplete={autoComplete}
          className={`
                        w-full px-3 py-2.5
                        ${icon ? 'pl-10' : ''}
                        ${error ? 'pr-10' : success ? 'pr-10' : ''}
                        bg-white dark:bg-gray-800
                        border rounded-lg
                        text-sm text-gray-900 dark:text-white
                        placeholder-gray-400 dark:placeholder-gray-500
                        transition-all duration-200
                        focus:outline-none focus:ring-2 focus:ring-offset-0
                        disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed
                        ${readOnly ? 'bg-gray-50 dark:bg-gray-900/50 cursor-default' : ''}
                        ${
                          error
                            ? 'border-error-300 focus:border-error-500 focus:ring-error-500/20'
                            : success
                              ? 'border-success-300 focus:border-success-500 focus:ring-success-500/20'
                              : 'border-gray-300 dark:border-gray-600 focus:border-primary-500 focus:ring-primary-500/20 hover:border-gray-400 dark:hover:border-gray-500'
                        }
                    `}
          {...rest}
        />

        {/* Floating Label */}
        <motion.label
          htmlFor={id}
          initial={false}
          animate={{
            top: isFloating ? '-0.5rem' : '50%',
            y: isFloating ? 0 : '-50%',
            fontSize: isFloating ? '0.75rem' : '0.875rem',
            left: icon && !isFloating ? '2.5rem' : '0.75rem'
          }}
          transition={{ duration: 0.2 }}
          className={`
                        absolute px-1
                        bg-white dark:bg-gray-800
                        pointer-events-none
                        transition-colors duration-200
                        ${
                          error
                            ? 'text-error-600'
                            : success
                              ? 'text-success-600'
                              : isFocused
                                ? 'text-primary-600'
                                : 'text-gray-500 dark:text-gray-400'
                        }
                    `}
        >
          {label}
          {required && <span className="text-error-500 ml-1">*</span>}
        </motion.label>

        {/* Status Icons */}
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2">
          <AnimatePresence mode="wait">
            {error && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <AlertCircle className="w-4 h-4 text-error-500" />
              </motion.div>
            )}
            {success && !error && (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
              >
                <CheckCircle2 className="w-4 h-4 text-success-500" />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Error Message */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0, y: -10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <p className="text-error-600 text-sm mt-2 flex items-start">
              <AlertCircle className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Helper Text */}
      {!error && placeholder && !isFocused && (
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-2">{placeholder}</p>
      )}
    </div>
  )
}

FormInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  type: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  error: PropTypes.string,
  success: PropTypes.bool,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  placeholder: PropTypes.string,
  icon: PropTypes.node,
  autoComplete: PropTypes.string
}

export default FormInput
